import Axios, { AxiosResponse } from 'axios';
import type { SupportedLanguages } from '@/plugins/i18n';
import type * as ApiModel from './models.pleased';
import { attachAxiosErrorTracking, captureApiFailure } from '@/common/error-tracking';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** When true, interceptors / throwIfError skip GlitchTip API capture (caller uses captureEventException). */
    skipErrorTracking?: boolean;
  }
}

/**
 * Shared HTTP client instance for all API requests
 * Configured with default headers for Pleased API
 */
export const httpClient = Axios.create({
  headers: {
    'pl-access-token': '',
    'brand-subdomain': '',
    language: 'en',
  },
});

attachAxiosErrorTracking(httpClient);

/**
 * Success response code from Pleased API
 */
export const SUCCESS_CODE = 10000;

export interface PleaseApiConfig {
  url?: string;
  brandCode?: string;
  token?: string | null;
  acceptLanguage?: SupportedLanguages | '*' | '';
}

/**
 * Conditionally set the none empty configurations.
 */
export function setConfig({ url = '', brandCode = '', token = '', acceptLanguage = '' }: PleaseApiConfig) {
  url && (httpClient.defaults.baseURL = url);
  brandCode && (httpClient.defaults.headers['brand-subdomain'] = brandCode);
  token && (httpClient.defaults.headers['pl-access-token'] = token);
  acceptLanguage && (httpClient.defaults.headers['language'] = acceptLanguage);
}

type TrackingAxiosConfig = { skipErrorTracking?: boolean };

/**
 * Utility that throws error if an API error has occurred.
 * @param response The response from API
 * @param throwResponseData When true, throws `response.data` (includes `code`) instead of `Error`
 */
export function throwIfError(response: AxiosResponse<ApiModel.ApiResponse<unknown>>, throwResponseData = false) {
  if (response.data.code !== SUCCESS_CODE) {
    const url = response.config.url;
    const skipErrorTracking = (response.config as TrackingAxiosConfig | undefined)?.skipErrorTracking;
    // Import dynamically to avoid circular dependency
    import('./logger').then(({ logErrorEvents }) => {
      logErrorEvents({
        event: 'API Error',
        message: response.data.message,
        url,
        code: response.data.code,
      });
    });

    if (!skipErrorTracking) {
      captureApiFailure({
        event: 'API Error',
        url,
        method: response.config.method,
        status: response.status,
        code: response.data.code,
      });
    }

    if (throwResponseData) {
      throw response.data;
    }

    // Keep Pleased `code` on the throw so `captureEventException` can fingerprint by business code
    // instead of the per-request correlation suffix in `message`.
    throw Object.assign(new Error(response.data.message), { code: response.data.code });
  }
}
